import { Suspense } from 'react';
import type { ReactNode } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AppShell } from '@invento/ui/blocks/layout';
import { CompatibilityRedirectNotice } from '@invento/ui/components/compatibility-redirect-notice';
import { Sidebar } from './sidebar';
import { MobileNav } from './mobile-nav';
import { ProductionShellProvider } from './production-shell-context';
import { useUIStore } from '@/state/stores/ui.store';
import { ROUTE_PATHS } from '@/navigation';
import { RouteLoadingScreen } from '@/navigation/route-loading-screen';
import { RouteErrorBoundary } from '@/navigation/route-error-boundary';
import type { CompatibilityRedirectState } from '@/navigation/compatibility-redirect';

function isRouteUnder(pathname: string, root: string) {
  return pathname === root || pathname.startsWith(`${root}/`);
}

function compatibilityDismissalKey(legacyPath: string) {
  return `invento.compatibility-redirect.dismissed:${legacyPath}`;
}

export function AppLayout({ children }: { children?: ReactNode }) {
  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed);
  const location = useLocation();
  const navigate = useNavigate();
  const redirectState = location.state as CompatibilityRedirectState | null;
  const compatibilityRedirect = redirectState?.compatibilityRedirect;
  const compatibilityDismissed = Boolean(
    compatibilityRedirect?.legacyPath &&
      typeof window !== 'undefined' &&
      window.sessionStorage.getItem(compatibilityDismissalKey(compatibilityRedirect.legacyPath)) === '1',
  );
  const isFullBleedRoute =
    location.pathname === ROUTE_PATHS.HOME ||
    isRouteUnder(location.pathname, ROUTE_PATHS.INVENTORY.ROOT) ||
    isRouteUnder(location.pathname, ROUTE_PATHS.SALES.ROOT) ||
    isRouteUnder(location.pathname, ROUTE_PATHS.PURCHASES.ROOT) ||
    isRouteUnder(location.pathname, ROUTE_PATHS.CUSTOMERS.ROOT) ||
    isRouteUnder(location.pathname, ROUTE_PATHS.MONEY.ROOT) ||
    isRouteUnder(location.pathname, ROUTE_PATHS.TEAM.ROOT) ||
    location.pathname === ROUTE_PATHS.REPORTS ||
    location.pathname === ROUTE_PATHS.SETTINGS ||
    location.pathname === ROUTE_PATHS.HELP ||
    location.pathname === ROUTE_PATHS.MENU;

  return (
    <ProductionShellProvider>
      <AppShell
        sidebar={<Sidebar />}
        mobileNav={<MobileNav />}
        sidebarCollapsed={sidebarCollapsed}
        fullBleed={isFullBleedRoute}
      >
        {compatibilityRedirect && !compatibilityDismissed ? (
          <CompatibilityRedirectNotice
            variant={compatibilityRedirect.variant}
            legacyPath={compatibilityRedirect.legacyPath}
            fromLabel={compatibilityRedirect.fromLabel}
            toLabel={compatibilityRedirect.toLabel}
            onDismiss={() => {
              if (typeof window !== 'undefined') {
                window.sessionStorage.setItem(
                  compatibilityDismissalKey(compatibilityRedirect.legacyPath),
                  '1',
                );
              }
              navigate(`${location.pathname}${location.search}${location.hash}`, {
                replace: true,
                state: null,
              });
            }}
          />
        ) : null}
        <RouteErrorBoundary>
          <Suspense fallback={<RouteLoadingScreen contained />}>
            {children ?? <Outlet />}
          </Suspense>
        </RouteErrorBoundary>
      </AppShell>
    </ProductionShellProvider>
  );
}
